// Sentry doit être importé en premier pour capturer les erreurs d'init React.
import "./sentry";
import { StrictMode, type ReactNode } from "react";
import { createRoot } from "react-dom/client";
import { ClerkProvider } from "@clerk/clerk-react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter } from "react-router";
import {
  Toaster,
  initColorThemeFromStorage,
  registerChunkLoadRecovery,
  resetChunkReloadAttempts,
} from "@cliently/ui";
import { Analytics } from "@vercel/analytics/react";
import { App } from "./App";
import { ErrorBoundary } from "@cliently/ui";
import { isAuthV2 } from "@cliently/auth-client";
import "./i18n";
import "./index.css";

initColorThemeFromStorage();
registerChunkLoadRecovery();
resetChunkReloadAttempts();

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5,
      retry: 1,
    },
  },
});

const root = createRoot(document.getElementById("root")!);

/**
 * Coquille commune React-Query + Router + UI. Indépendante du fournisseur
 * d'auth pour permettre la coexistence Clerk / Auth v2.
 */
const renderShell = (children: ReactNode) =>
  root.render(
    <StrictMode>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <ErrorBoundary>
            {children}
            <Toaster />
            <Analytics />
          </ErrorBoundary>
        </BrowserRouter>
      </QueryClientProvider>
    </StrictMode>,
  );

if (isAuthV2()) {
  // PATCH TEMPORAIRE DE TEST — À RETIRER.
  // Header.tsx et AccountTab.tsx appellent useClerk()/useUser() sans garde, ce
  // qui fait planter toute page authentifiée en mode v2. On monte un
  // ClerkProvider inerte pour satisfaire ces hooks ; la session reste gérée
  // par auth-v2.
  const inertClerkKey =
    import.meta.env.VITE_CLERK_PUBLISHABLE_KEY?.trim() || "";
  renderShell(
    inertClerkKey ? (
      <ClerkProvider publishableKey={inertClerkKey}>
        <App />
      </ClerkProvider>
    ) : (
      <App />
    ),
  );
} else {
  const clerkPubKey =
    import.meta.env.VITE_CLERK_PUBLISHABLE_KEY?.trim() || "";

  if (!clerkPubKey) {
    root.render(
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          height: "100vh",
          fontFamily: "system-ui",
          gap: "1rem",
          padding: "2rem",
          textAlign: "center",
        }}
      >
        <h1 style={{ fontSize: "1.5rem", fontWeight: 700 }}>
          Configuration manquante
        </h1>
        <p style={{ color: "#666", maxWidth: "28rem" }}>
          La variable{" "}
          <code
            style={{
              background: "#f1f1f1",
              padding: "2px 6px",
              borderRadius: 4,
            }}
          >
            VITE_CLERK_PUBLISHABLE_KEY
          </code>{" "}
          n'est pas definie. Creez un fichier{" "}
          <code
            style={{
              background: "#f1f1f1",
              padding: "2px 6px",
              borderRadius: 4,
            }}
          >
            apps/pro/.env
          </code>{" "}
          avec votre cle Clerk, ou passez{" "}
          <code
            style={{
              background: "#f1f1f1",
              padding: "2px 6px",
              borderRadius: 4,
            }}
          >
            VITE_AUTH_PROVIDER=v2
          </code>{" "}
          pour utiliser l'auth maison.
        </p>
      </div>,
    );
  } else {
    renderShell(
      <ClerkProvider publishableKey={clerkPubKey}>
        <App />
      </ClerkProvider>,
    );
  }
}
